import { useMemo } from 'react';
import { graphqlQuery } from '@/lib/api/graphql';
import {
  buildGraphQLSortVariable as buildSort,
  computeMockPagination,
  computePaginationWithCount,
  createDefaultPagination,
  type GraphQLConnection,
  type PaginationResult,
  pageToCursor,
} from '@/lib/graphql-pagination';
import { generateMockTransactions } from '@/lib/mock-transactions';
import { useApiConfig } from '@/providers/ApiConfigProvider';
import type { Transaction, TransactionStatus, TransactionType } from '@/types/transaction';
import { useModeAwareQuery } from './useModeAwareQuery';

// --- GraphQL node type for create promise transactions ---

interface CreatePromiseTransactionGraphQLNode {
  transactionId: number;
  referenceNumber: string | null;
  customerId: number;
  customerName: string | null;
  accountNumber: string | null;
  transactionDate: string;
  dueDate: string | null;
  anticipatedDate: string | null;
  amount: number;
  netAmount: number | null;
  vatAmount: number | null;
  outstandingAmount: number;
  description: string | null;
  transactionNotes: string | null;
  headerReference: string | null;
  accountCode: string | null;
  onDispute: boolean;
  hasDispute: boolean;
  subLedger: string | null;
  subLedgerName: string | null;
  orderNumber: string | null;
  documentUrl: string | null;
  transactionReference: string | null;
  bankReference: string | null;
  currencyAmount: number | null;
  currencyNetAmount: number | null;
  currencyVatAmount: number | null;
  currencyOutstandingAmount: number | null;
  displayAmount: number | null;
  displayNetAmount: number | null;
  displayVatAmount: number | null;
  displayOutstandingAmount: number | null;
  daysOverdue: number;
  transactionCreatedAt: string;
  transactionModifiedAt: string | null;
  currencyCode: string | null;
  transactionTypeCode: string | null;
  transactionTypeName: string | null;
  transactionStatusCode: string | null;
  transactionStatusName: string | null;
  paymentMethodName: string | null;
  createdByName: string | null;
  modifiedByName: string | null;
  ageingBucket: string | null;
  totalAllocated: number;
  allocationCount: number;
  allocationRef: string | null;
  allocationStatusCode: string | null;
  allocationStatusName: string | null;
  hasPromise: boolean;
  hasCase: boolean;
  externalId: string | null;
}

interface CreatePromiseTransactionListGraphQLResponse {
  createPromiseTransactions: {
    createPromiseTransactionList: GraphQLConnection<CreatePromiseTransactionGraphQLNode>;
  };
}

interface CreatePromiseTransactionCountGraphQLResponse {
  createPromiseTransactions: {
    createPromiseTransactionCount: number;
  };
}

interface CreatePromiseTransactionTotalsGraphQLResponse {
  createPromiseTransactions: {
    createPromiseTransactionTotals: {
      totalCount: number;
      totalOutstanding: number;
    };
  };
}

// --- GraphQL queries ---

const CREATE_PROMISE_TRANSACTION_LIST_QUERY = `
  query CreatePromiseTransactionList($first: Int, $after: String, $sort: [CreatePromiseTransactionListSortSortByInput!], $filter: CreatePromiseTransactionListFilterInput) {
    createPromiseTransactions {
      createPromiseTransactionList(first: $first, after: $after, sort: $sort, filter: $filter) {
        edges {
          node {
            transactionId
            referenceNumber
            customerId
            customerName
            accountNumber
            transactionDate
            dueDate
            anticipatedDate
            amount
            netAmount
            vatAmount
            outstandingAmount
            description
            transactionNotes
            headerReference
            accountCode
            onDispute
            hasDispute
            subLedger
            subLedgerName
            orderNumber
            documentUrl
            transactionReference
            bankReference
            currencyAmount
            currencyNetAmount
            currencyVatAmount
            currencyOutstandingAmount
            displayAmount
            displayNetAmount
            displayVatAmount
            displayOutstandingAmount
            daysOverdue
            transactionCreatedAt
            transactionModifiedAt
            currencyCode
            transactionTypeCode
            transactionTypeName
            transactionStatusCode
            transactionStatusName
            paymentMethodName
            createdByName
            modifiedByName
            ageingBucket
            totalAllocated
            allocationCount
            allocationRef
            allocationStatusCode
            allocationStatusName
            hasPromise
            hasCase
            externalId
          }
        }
        pageInfo {
          startCursor
          endCursor
          hasNextPage
          hasPreviousPage
        }
      }
    }
  }
`;

const CREATE_PROMISE_TRANSACTION_COUNT_QUERY = `
  query CreatePromiseTransactionCount($filter: CreatePromiseTransactionListFilterInput) {
    createPromiseTransactions {
      createPromiseTransactionCount(filter: $filter)
    }
  }
`;

const CREATE_PROMISE_TRANSACTION_TOTALS_QUERY = `
  query CreatePromiseTransactionTotals($filter: CreatePromiseTransactionListFilterInput) {
    createPromiseTransactions {
      createPromiseTransactionTotals(filter: $filter) {
        totalCount
        totalOutstanding
      }
    }
  }
`;

// --- Sort field map ---

const createPromiseSortFieldMap: Record<string, string> = {
  referenceNumber: 'ReferenceNumber',
  transactionDate: 'TransactionDate',
  dueDate: 'DueDate',
  anticipatedDate: 'AnticipatedDate',
  customerName: 'CustomerName',
  transactionType: 'TransactionTypeName',
  amount: 'Amount',
  outstandingAmount: 'OutstandingAmount',
  status: 'TransactionStatusCode',
  currency: 'CurrencyCode',
  description: 'Description',
  createdBy: 'CreatedByName',
  modifiedBy: 'ModifiedByName',
  accountCode: 'AccountCode',
  netAmount: 'NetAmount',
  vatAmount: 'VatAmount',
  onDispute: 'OnDispute',
  subLedger: 'SubLedger',
  orderNumber: 'OrderNumber',
  currencyGross: 'CurrencyAmount',
  currencyNet: 'CurrencyNetAmount',
  currencyVAT: 'CurrencyVatAmount',
  currencyOutstanding: 'CurrencyOutstandingAmount',
};

// --- Mapping ---

function mapTransactionTypeCode(code: string | null): TransactionType {
  const typeMap: Record<string, TransactionType> = {
    invoice: 'invoice',
    credit_note: 'credit_note',
    receipt: 'receipt',
    debit_adjustment: 'debit_adjustment',
    credit_adjustment: 'credit_adjustment',
  };
  return typeMap[(code ?? '').toLowerCase()] ?? 'invoice';
}

function mapTransactionStatusCode(code: string | null): TransactionStatus {
  const statusMap: Record<string, TransactionStatus> = {
    open: 'outstanding',
    paid: 'completed',
    overdue: 'overdue',
  };
  return statusMap[(code ?? '').toLowerCase()] ?? 'outstanding';
}

function mapGraphQLNodeToTransaction(node: CreatePromiseTransactionGraphQLNode): Transaction {
  return {
    id: node.transactionId,
    referenceNumber: node.referenceNumber || String(node.transactionId),
    transactionDate: node.transactionDate,
    customerId: node.customerId,
    customerName: node.customerName ?? '',
    transactionType: mapTransactionTypeCode(node.transactionTypeCode),
    amount: node.amount,
    currency: node.currencyCode ?? 'GBP',
    status: mapTransactionStatusCode(node.transactionStatusCode),
    description: node.description ?? '',
    notes: node.transactionNotes ?? undefined,
    createdBy: node.createdByName ?? '',
    modifiedBy: node.modifiedByName ?? undefined,
    createdAt: node.transactionCreatedAt,
    modifiedAt: node.transactionModifiedAt ?? undefined,
    dueDate: node.dueDate ?? undefined,
    anticipatedDate: node.anticipatedDate ?? undefined,
    headerReference: node.headerReference ?? undefined,
    paymentMethod: node.paymentMethodName ?? undefined,
    documentUrl: node.documentUrl ?? undefined,
    outstandingAmount: node.outstandingAmount,
    onDispute: node.onDispute,
    hasDispute: node.hasDispute,
    accountCode: node.accountCode ?? undefined,
    netAmount: node.netAmount ?? undefined,
    vatAmount: node.vatAmount ?? undefined,
    subLedger: node.subLedger ?? undefined,
    subLedgerName: node.subLedgerName ?? undefined,
    orderNumber: node.orderNumber ?? undefined,
    transactionReference: node.transactionReference ?? undefined,
    bankReference: node.bankReference ?? undefined,
    currencyGross: node.currencyAmount ?? undefined,
    currencyNet: node.currencyNetAmount ?? undefined,
    currencyVAT: node.currencyVatAmount ?? undefined,
    currencyOutstanding: node.currencyOutstandingAmount ?? undefined,
    displayAmount: node.displayAmount ?? undefined,
    displayNetAmount: node.displayNetAmount ?? undefined,
    displayVatAmount: node.displayVatAmount ?? undefined,
    displayOutstandingAmount: node.displayOutstandingAmount ?? undefined,
    accountNumber: node.accountNumber ?? undefined,
    daysOverdue: node.daysOverdue,
    ageingBucket: node.ageingBucket ?? '',
    hasPromise: node.hasPromise,
    hasCase: node.hasCase,
    allocationStatusCode: node.allocationStatusCode ?? '',
    allocationStatusName: node.allocationStatusName ?? '',
    totalAllocated: node.totalAllocated,
    allocationCount: node.allocationCount,
    allocationRef: node.allocationRef ?? undefined,
    externalId: node.externalId ?? undefined,
  };
}

// --- Filter builder ---

export interface GetCreatePromiseTransactionsParams {
  customerId?: string;
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  transactionIds?: number[];
}

function buildCreatePromiseTransactionFilter(
  customerId: string | undefined,
  params?: GetCreatePromiseTransactionsParams,
): Record<string, unknown> | undefined {
  const filter: Record<string, unknown> = {};

  if (customerId) {
    filter.customerId = { eq: Number(customerId) };
  }

  if (params?.search) {
    filter.summary = params.search;
  }

  if (params?.transactionIds && params.transactionIds.length > 0) {
    filter.transactionId = { containsValues: params.transactionIds };
  }

  return Object.keys(filter).length > 0 ? filter : undefined;
}

// --- Result type ---

export interface CreatePromiseTransactionsGraphQLResult {
  transactions: Transaction[];
  pagination: PaginationResult;
  totalOutstanding: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// --- Hook ---

const defaultPagination: PaginationResult = createDefaultPagination(25);

interface CreatePromiseTransactionsListResult {
  transactions: Transaction[];
  pagination: PaginationResult;
}

export function useCreatePromiseTransactionsPaginatedGraphQL(
  customerId: string | undefined,
  params?: GetCreatePromiseTransactionsParams,
): CreatePromiseTransactionsGraphQLResult {
  const apiConfig = useApiConfig();

  const queryParams: GetCreatePromiseTransactionsParams = {
    page: 1,
    pageSize: 25,
    sortBy: 'transactionDate',
    sortOrder: 'desc',
    ...params,
  };

  const page = queryParams.page ?? 1;
  const pageSize = queryParams.pageSize ?? 25;

  // Build mock transactions filtered to the selected customer, with an outstanding balance
  const { mockListResult, mockCount, mockTotalOutstanding } = useMemo(() => {
    const allMockTransactions = generateMockTransactions();
    const customerIdNum = customerId ? parseInt(customerId, 10) : undefined;
    const filtered = allMockTransactions.filter((t) => {
      if (customerIdNum !== undefined && t.customerId !== customerIdNum) return false;
      // Only show transactions with an outstanding balance (eligible for case/promise creation)
      return (t.outstandingAmount ?? 0) > 0;
    });
    const search = queryParams.search?.toLowerCase();
    const searched = search
      ? filtered.filter(
          (t) => t.referenceNumber?.toLowerCase().includes(search) || t.description?.toLowerCase().includes(search),
        )
      : filtered;
    const totalOutstanding = filtered.reduce((sum, t) => sum + (t.outstandingAmount ?? 0), 0);
    return {
      mockListResult: {
        transactions: searched,
        pagination: computeMockPagination(searched.length, page, pageSize),
      } as CreatePromiseTransactionsListResult,
      mockCount: searched.length,
      mockTotalOutstanding: totalOutstanding,
    };
  }, [customerId, queryParams.search, page, pageSize]);

  const filter = buildCreatePromiseTransactionFilter(customerId, queryParams);

  // --- List query ---
  const listQuery = useModeAwareQuery<CreatePromiseTransactionsListResult, Error>(
    ['create-promise-transactions-graphql-list', customerId, queryParams],
    async ({ signal }) => {
      if (!customerId) {
        return { transactions: [], pagination: computeMockPagination(0, 1, pageSize) };
      }

      const variables: Record<string, unknown> = {
        first: pageSize,
        sort: buildSort(createPromiseSortFieldMap, queryParams.sortBy, queryParams.sortOrder),
        filter,
      };

      if (page > 1) {
        variables.after = pageToCursor(page);
      }

      const response = await graphqlQuery<CreatePromiseTransactionListGraphQLResponse>(
        apiConfig,
        CREATE_PROMISE_TRANSACTION_LIST_QUERY,
        variables,
        signal,
      );

      const connection = response.createPromiseTransactions.createPromiseTransactionList;
      const transactions = connection.edges.map((x) => mapGraphQLNodeToTransaction(x.node));

      const pagination: PaginationResult = {
        page,
        pageSize,
        totalItems: transactions.length,
        totalPages: 1,
        hasNextPage: connection.pageInfo.hasNextPage,
        hasPreviousPage: connection.pageInfo.hasPreviousPage,
      };

      return { transactions, pagination };
    },
    mockListResult,
    {
      enabled: !!customerId,
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );

  // --- Count query ---
  const countQuery = useModeAwareQuery<number, Error>(
    ['create-promise-transactions-graphql-count', customerId, filter],
    async ({ signal }) => {
      if (!customerId) return 0;

      const variables: Record<string, unknown> = {};
      if (filter) {
        variables.filter = filter;
      }

      const response = await graphqlQuery<CreatePromiseTransactionCountGraphQLResponse>(
        apiConfig,
        CREATE_PROMISE_TRANSACTION_COUNT_QUERY,
        variables,
        signal,
      );

      return response.createPromiseTransactions.createPromiseTransactionCount;
    },
    mockCount,
    {
      enabled: !!customerId,
      staleTime: 30_000,
      refetchOnMount: 'always',
    },
  );

  // --- Totals query (filtered by customerId and transactionIds, no search) ---
  // This ensures Total Outstanding stays stable when the user searches/filters
  // but still reflects the correct scope when filtered to specific transactions
  const totalsFilter = buildCreatePromiseTransactionFilter(customerId, { transactionIds: params?.transactionIds });

  const totalsQuery = useModeAwareQuery<{ totalCount: number; totalOutstanding: number }, Error>(
    ['create-promise-transactions-graphql-totals', customerId, totalsFilter],
    async ({ signal }) => {
      if (!customerId) return { totalCount: 0, totalOutstanding: 0 };

      const variables: Record<string, unknown> = {};
      if (totalsFilter) {
        variables.filter = totalsFilter;
      }

      const response = await graphqlQuery<CreatePromiseTransactionTotalsGraphQLResponse>(
        apiConfig,
        CREATE_PROMISE_TRANSACTION_TOTALS_QUERY,
        variables,
        signal,
      );

      const totals = response.createPromiseTransactions.createPromiseTransactionTotals;
      return {
        totalCount: totals.totalCount,
        totalOutstanding: totals.totalOutstanding,
      };
    },
    { totalCount: mockCount, totalOutstanding: mockTotalOutstanding },
    {
      enabled: !!customerId,
      staleTime: 30_000,
      refetchOnMount: 'always',
    },
  );

  // --- Combine results ---
  const basePagination = listQuery.data?.pagination ?? defaultPagination;
  const totalCount = countQuery.data ?? 0;
  const pagination = computePaginationWithCount(basePagination, totalCount, params?.pageSize);

  return {
    transactions: listQuery.data?.transactions ?? [],
    pagination,
    totalOutstanding: totalsQuery.data?.totalOutstanding ?? 0,
    isLoading: listQuery.isLoading,
    error: listQuery.error,
    refetch: () => {
      listQuery.refetch();
      countQuery.refetch();
      totalsQuery.refetch();
    },
  };
}

// --- Export filter builder (for useExportAll) ---

export function buildCreatePromiseTransactionExportFilter(
  customerId: string | undefined,
  search?: string,
  transactionIds?: number[],
): Record<string, unknown> | undefined {
  const filter: Record<string, unknown> = {};

  if (customerId) {
    filter.customerId = { eq: Number(customerId) };
  }

  if (search) {
    filter.summary = search;
  }

  if (transactionIds && transactionIds.length > 0) {
    filter.transactionId = { containsValues: transactionIds };
  }

  return Object.keys(filter).length > 0 ? filter : undefined;
}

// --- Column name map for export ---

export const CREATE_PROMISE_TRANSACTION_EXPORT_COLUMN_MAP: Record<string, string> = {
  referenceNumber: 'ReferenceNumber',
  transactionDate: 'TransactionDate',
  dueDate: 'DueDate',
  anticipatedDate: 'AnticipatedDate',
  transactionType: 'TransactionTypeName',
  amount: 'Amount',
  currency: 'CurrencyCode',
  outstandingAmount: 'OutstandingAmount',
  status: 'TransactionStatusName',
  description: 'Description',
  accountCode: 'AccountCode',
  netAmount: 'NetAmount',
  vatAmount: 'VatAmount',
  subLedger: 'SubLedger',
  orderNumber: 'OrderNumber',
  createdBy: 'CreatedByName',
  modifiedBy: 'ModifiedByName',
  customerName: 'CustomerName',
  onDispute: 'OnDispute',
  flags: 'HasDispute',
};
